import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { StatusCodes } from 'http-status-codes'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { formatCentsInEuros } from '../../domain/GuestAdoption/guestAdoptionDonation'
import {
  GuestAdoptionCheckoutDto,
  GuestAdoptionContactBodyDto,
  GuestAdoptionPaymentIntentDto,
  GuestAdoptionsClient,
} from '../../interactors/clients/GuestAdoptionsClient'
import { AuthService } from '../../interactors/services/AuthService'
import { useAccountsStore } from '../../store/AccountsStore'
import { useGlobalSnackbarStore } from '../../store/GlobalSnackBarStore'
import { theme } from '../theme'
import { GuestAdoptionContactForm } from './GuestAdoptionContactForm'
import { GuestAdoptionDonationStep } from './GuestAdoptionDonationStep'
import {
  GuestAdoptionAmountRow,
  GuestAdoptionCard,
  GuestAdoptionLayout,
  GuestAdoptionTitle,
} from './GuestAdoptionLayout'
import { GuestAdoptionPaymentStep } from './GuestAdoptionPaymentStep'
import { GuestAdoptionRecap } from './GuestAdoptionRecap'
import { GuestAdoptionWelcome } from './GuestAdoptionWelcome'

type Step = 'summary' | 'contact' | 'donation' | 'payment' | 'confirming' | 'paid'
type LinkError = 'invalid' | 'expired' | 'unknown'

// The Stripe webhook usually lands in less than a second.
const POLLING_INTERVAL_IN_MS = 2000
const MAX_POLLING_ATTEMPTS = 15

const wait = (durationInMs: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, durationInMs))

const getLinkError = (error: unknown): LinkError => {
  switch (GuestAdoptionsClient.getErrorStatus(error)) {
    case StatusCodes.NOT_FOUND:
      return 'invalid'
    case StatusCodes.GONE:
      return 'expired'
    default:
      return 'unknown'
  }
}

export const GuestAdoptionPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const accountsStore = useAccountsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  const [checkout, setCheckout] = useState<GuestAdoptionCheckoutDto | null>(null)
  const [linkError, setLinkError] = useState<LinkError | null>(null)
  const [step, setStep] = useState<Step>('summary')
  const [paymentIntent, setPaymentIntent] = useState<GuestAdoptionPaymentIntentDto | null>(null)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [justPaid, setJustPaid] = useState<boolean>(false)
  const [sessionOpened, setSessionOpened] = useState<boolean>(false)
  const [confirmationTimedOut, setConfirmationTimedOut] = useState<boolean>(false)
  const [loadingAttempt, setLoadingAttempt] = useState<number>(0)
  const [pollingAttempt, setPollingAttempt] = useState<number>(0)

  // Exchanges the paid checkout link for a regular adopter session, stored exactly like a login.
  const openSession = async (): Promise<boolean> => {
    if (!token) {
      return false
    }

    try {
      const accessToken = await GuestAdoptionsClient.createSession(token)
      const account = await AuthService.loginWithAccessToken(accessToken)
      accountsStore.changeConnectedAccount(account)
      setSessionOpened(true)

      return true
    } catch {
      return false
    }
  }

  useEffect(() => {
    if (!token) {
      return setLinkError('invalid')
    }

    let cancelled = false

    GuestAdoptionsClient.getCheckout(token)
      .then((loadedCheckout) => {
        if (cancelled) {
          return
        }

        // Payment methods which leave the page bring the adopter back with the outcome in the query string.
        const redirectStatus = new URLSearchParams(window.location.search).get('redirect_status')
        if (redirectStatus) {
          navigate({ search: '' }, { replace: true })
        }

        setLinkError(null)
        setCheckout(loadedCheckout)
        // The adopter may already be connected on this device.
        setSessionOpened(AuthService.account?.id === loadedCheckout.adopter.id)

        if (loadedCheckout.status === 'paid') {
          return setStep('paid')
        }

        if (redirectStatus === 'failed') {
          setPaymentError('Votre paiement a échoué. Veuillez réessayer.')
          return setStep('donation')
        }

        if (redirectStatus) {
          setStep('confirming')
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setLinkError(getLinkError(error))
        }
      })

    return () => {
      cancelled = true
    }
  }, [token, loadingAttempt])

  useEffect(() => {
    if (step !== 'confirming' || !token) {
      return
    }

    let cancelled = false

    const waitForPaymentConfirmation = async (): Promise<void> => {
      for (let attempt = 0; attempt < MAX_POLLING_ATTEMPTS; attempt++) {
        try {
          const refreshedCheckout = await GuestAdoptionsClient.getCheckout(token)
          if (cancelled) {
            return
          }

          if (refreshedCheckout.status === 'paid') {
            setCheckout(refreshedCheckout)
            setJustPaid(true)
            await openSession()

            return setStep('paid')
          }
        } catch {
          // A network hiccup must not interrupt the wait: the next attempt tells.
        }

        await wait(POLLING_INTERVAL_IN_MS)
        if (cancelled) {
          return
        }
      }

      setConfirmationTimedOut(true)
    }

    waitForPaymentConfirmation()

    return () => {
      cancelled = true
    }
  }, [step, token, pollingAttempt])

  const reloadCheckout = () => {
    setLinkError(null)
    setLoadingAttempt(loadingAttempt + 1)
  }

  // The link may be paid, expired or replaced while the page is open: the status of the error tells what to show.
  const handleCheckoutError = (error: unknown): 'handled' | 'unhandled' => {
    switch (GuestAdoptionsClient.getErrorStatus(error)) {
      case StatusCodes.CONFLICT:
        globalSnackbarStore.triggerInfoMessage('Cette adoption est déjà payée.')
        setCheckout(null)
        reloadCheckout()
        return 'handled'
      case StatusCodes.NOT_FOUND:
        setLinkError('invalid')
        return 'handled'
      case StatusCodes.GONE:
        setLinkError('expired')
        return 'handled'
      default:
        return 'unhandled'
    }
  }

  const handleContactSubmit = async (contact: GuestAdoptionContactBodyDto): Promise<void> => {
    if (!token) {
      return
    }

    try {
      const updatedCheckout = await GuestAdoptionsClient.updateContact(token, contact)
      setCheckout(updatedCheckout)
      setStep('summary')
      globalSnackbarStore.triggerSuccessMessage('Vos coordonnées ont bien été enregistrées.')
    } catch (error) {
      if (handleCheckoutError(error) === 'unhandled') {
        // The form displays its own error message. The error itself is not forwarded: its request URL holds the token.
        throw new Error('The contact details could not be saved')
      }
    }
  }

  const handleDonationSubmit = async (donationInCents: number): Promise<void> => {
    if (!token || !checkout) {
      return
    }

    setPaymentError(null)

    try {
      const createdPaymentIntent = await GuestAdoptionsClient.createPaymentIntent(token, donationInCents)
      setPaymentIntent(createdPaymentIntent)
      setStep('payment')
    } catch (error) {
      if (handleCheckoutError(error) === 'unhandled') {
        setPaymentError(
          GuestAdoptionsClient.getErrorStatus(error) === StatusCodes.BAD_REQUEST
            ? `Le paiement en ligne n'est pas disponible pour le moment. Contactez ${checkout.organization.name} pour finaliser votre adoption.`
            : 'Une erreur est survenue. Veuillez réessayer.'
        )
      }
    }
  }

  const retryPaymentConfirmation = () => {
    setConfirmationTimedOut(false)
    setPollingAttempt(pollingAttempt + 1)
  }

  const renderContent = () => {
    if (linkError === 'invalid') {
      return (
        <GuestAdoptionCard>
          <GuestAdoptionTitle>Ce lien n&apos;est pas valide</GuestAdoptionTitle>
          <Typography variant="body1">
            Vérifiez que vous avez bien ouvert le lien complet reçu de l&apos;association. Si le problème persiste,
            contactez l&apos;association qui vous l&apos;a transmis : elle pourra vous en envoyer un nouveau.
          </Typography>
        </GuestAdoptionCard>
      )
    }

    if (linkError === 'expired') {
      return (
        <GuestAdoptionCard>
          <GuestAdoptionTitle>Ce lien a expiré</GuestAdoptionTitle>
          <Typography variant="body1">
            Demandez-en un nouveau à l&apos;association pour finaliser votre adoption.
          </Typography>
        </GuestAdoptionCard>
      )
    }

    if (linkError === 'unknown') {
      return (
        <GuestAdoptionCard>
          <GuestAdoptionTitle>Une erreur est survenue</GuestAdoptionTitle>
          <Typography variant="body1">
            Nous n&apos;avons pas pu charger votre adoption. Vérifiez votre connexion puis réessayez.
          </Typography>
          <Button variant="contained" color="primary" fullWidth sx={{ fontSize: '18px' }} onClick={reloadCheckout}>
            Réessayer
          </Button>
        </GuestAdoptionCard>
      )
    }

    if (!checkout) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
          <CircularProgress />
        </Box>
      )
    }

    if (step === 'paid') {
      return (
        <GuestAdoptionWelcome
          checkout={checkout}
          justPaid={justPaid}
          sessionOpened={sessionOpened}
          openSession={openSession}
        />
      )
    }

    if (step === 'confirming') {
      return (
        <GuestAdoptionCard>
          <GuestAdoptionTitle>Confirmation du paiement</GuestAdoptionTitle>
          {confirmationTimedOut ? (
            <>
              <Typography variant="body1">
                {`Votre paiement est en cours de confirmation, cela prend plus de temps que prévu. Inutile de payer à nouveau : vous recevrez un email à l'adresse ${checkout.adopter.email} dès qu'il sera confirmé.`}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ fontSize: '18px' }}
                onClick={retryPaymentConfirmation}
              >
                Vérifier à nouveau
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body1">
                Nous confirmons votre paiement, merci de patienter quelques instants…
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress />
              </Box>
            </>
          )}
        </GuestAdoptionCard>
      )
    }

    if (step === 'contact') {
      return (
        <GuestAdoptionContactForm
          adopter={checkout.adopter}
          onSubmit={handleContactSubmit}
          onCancel={() => setStep('summary')}
        />
      )
    }

    if (step === 'payment' && paymentIntent) {
      return (
        <GuestAdoptionPaymentStep
          animalName={checkout.animal.name}
          adopter={checkout.adopter}
          paymentIntent={paymentIntent}
          onPaid={() => setStep('confirming')}
          onBack={() => setStep('donation')}
        />
      )
    }

    if (step === 'donation') {
      return (
        <GuestAdoptionDonationStep
          animalName={checkout.animal.name}
          amountInCents={checkout.amountInCents}
          errorMessage={paymentError}
          onSubmit={handleDonationSubmit}
          onBack={() => setStep('summary')}
        />
      )
    }

    return (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          <GuestAdoptionTitle>{`Finalisez l'adoption de ${checkout.animal.name}`}</GuestAdoptionTitle>
          <Typography variant="body2" sx={{ color: '#696B6D', mt: 1 }}>
            {`Bonjour ${checkout.adopter.firstName}, il ne vous reste qu'à régler les frais d'adoption en ligne. Aucun compte à créer, aucun document à fournir.`}
          </Typography>
        </Box>
        <GuestAdoptionRecap checkout={checkout}>
          <Box sx={{ borderTop: '1px solid #CCCCCC', pt: 2 }}>
            <GuestAdoptionAmountRow label="Frais d'adoption :" amount={formatCentsInEuros(checkout.amountInCents)} />
          </Box>
        </GuestAdoptionRecap>
        <GuestAdoptionCard>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="h2" component="p" sx={{ color: '#4f4f4f', fontSize: '15px' }}>
              {`${checkout.adopter.firstName} ${checkout.adopter.lastName}`}
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f4f4f', fontSize: '13px' }}>
              {checkout.adopter.email}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '13px' }}>
              <span
                style={{ fontWeight: 700, color: theme.palette.primary.main, cursor: 'pointer' }}
                onClick={() => setStep('contact')}
              >
                Compléter mes coordonnées (facultatif)
              </span>
            </Typography>
          </Box>
        </GuestAdoptionCard>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          sx={{ fontSize: '18px' }}
          onClick={() => setStep('donation')}
        >
          Continuer
        </Button>
        <Typography variant="body2" sx={{ color: '#696B6D', fontSize: '12px', textAlign: 'center' }}>
          {`Paiement sécurisé par Stripe. Les frais d'adoption sont reversés à ${checkout.organization.name}.`}
        </Typography>
      </>
    )
  }

  return <GuestAdoptionLayout>{renderContent()}</GuestAdoptionLayout>
}
